import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles6() {
  const { lesson, course, accent } = findLesson("the-beatles", "6");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="The left hand" title="LHMH — the arpeggio pattern that plays a lot of Beethoven">
        <Prose>
          <p>
            Part 2 of "Because" focuses on how to play it. The left-hand arpeggio pattern Jason
            teaches is LHMH: <strong>Low, High, Middle, High</strong>. This is not the guitar's
            original arpeggio — that comes later, as a more advanced challenge. This is the
            working piano version, the one that gets you a "pretty decent working version" without
            the complexity of the original fingerpicking pattern.
          </p>
          <p>
            LHMH played as eighth notes: on C♯ minor, you take the low C♯, then the high note
            of the chord, then the middle, then the high again. Then you add the octave on top.
            "If you do this pattern you can play a lot of Beethoven songs," Jason observes —
            because the LHMH arpeggio is standard classical left-hand technique, not a Beatles
            invention. The Beatles simply used it for a song in C♯ harmonic minor.
          </p>
          <p>
            Pedalling matters here. For the D♯ half-diminished chord (D♯m7♭5) especially:
            lift before you hit it. Press, keep it pressed, then lift and press before the next
            chord. Harmonic minor chords clash if the pedal is held through them — the dissonance
            accumulates in an ugly way.
          </p>
        </Prose>

        <TheoryBox label="LHMH arpeggio pattern">
          L = lowest note of the chord (root, played in the bass). ·{" "}
          H = highest note. ·{" "}
          M = middle note. ·{" "}
          H = highest note again. ·{" "}
          Played as eighth notes, each L-H-M-H group fills two beats — two groups per bar of
          4/4 — a rolling, consistent texture. Add the octave jump on the high note for more range. Start here.
          Get this fluent. The guitar arpeggio can wait.
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="hL0tnrl2L_U"
        title="Because — The Beatles (arpeggio study)"
        caption="Listen to the arpeggio in the original: the harpsichord-like keyboard and the guitar roll through the chords together. Jason's LHMH pattern is a simpler working version of that layer for the piano — the guitar's own arpeggio comes later, as the advanced challenge."
      />

      <Chapter eyebrow="The modulation" title="D major — a semitone above the tonic. Who does that?">
        <Prose>
          <p>
            After establishing C♯ minor as home, after the A7 → A13 sequence, "Because" makes
            a modulation that makes Jason ask, "Who on earth will ever do that?" It goes to
            <strong> D major</strong>. D major is a semitone above C♯. That is one of the
            "probably the scariest notes you would ever want to modulate to." A semitone above
            your tonic. Nobody goes there.
          </p>
          <p>
            And then, within the D major section, the song does something even more unusual:
            D major flat five, moving down to D diminished. "This is not at all common," Jason
            says. "This is a major chord going down to a flat five and then the diminished. Okay,
            that's what makes the Beatles unique — this one move right here."
          </p>
          <p>
            Why does it work? Partly because of how the vocal harmony supports it. "Normally
            D diminished seventh won't go here. It's just somehow happening because of the vocal
            harmony." The vocal lines provide the logic that the harmony alone cannot. This is
            composition from the inside out — the singers create the context that makes the
            unusual chord progression sound inevitable.
          </p>
        </Prose>

        <RomanNumeralAnalysis
          label="The modulation sequence — C♯ minor → D major area"
          caption="The song leaves C♯ minor entirely, modulates to D (a semitone up), uses a D major flat 5 moving to D diminished. 'This part is not at all C sharp minor. But the rest is okay.' — Jason"
          chords={[
            { chord: "A", rn: "♭VI", function: "Pre-dominant" },
            { chord: "A7", rn: "V7/♭II", function: "Secondary V" },
            { chord: "D", rn: "♭II", function: "Borrowed" },
            { chord: "D(♭5)", rn: "♭II(♭5)", function: "Tension" },
            { chord: "Ddim7", rn: "♭ii°7", function: "Tension" },
            { chord: "C#m", rn: "i", function: "Tonic" },
          ]}
        />

        <TheoryBox label="Why the semitone modulation works at all">
          Normally modulations go to related keys: the relative major, the dominant, the
          subdominant. A move to the chord a semitone above the tonic sounds jarring because its
          root, D, is not in C♯ minor at all — even though the two scales still share five notes
          (C♯, E, F♯, A, B). In "Because," the A7 just before it acts as D's own five chord,
          and the vocal harmony carries the shared notes from chord to chord. The chords are
          daring; the voices make them logical.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The origin story" title="The Hamburg clubs — and why the Beatles got bored">
        <Prose>
          <p>
            To understand why the Beatles write harmony like this, Jason tells the origin story.
            Before they were famous, the Beatles spent long nights playing the clubs of Hamburg,
            Germany, across several residencies between 1960 and 1962. They played as daily
            clubbing musicians, hired to perform. What did they play? Everything. Every popular song of that era:
            rock and roll, blues, pop, jazz, whatever people wanted.
          </p>
          <p>
            "Literally every song which was popular, they have played it." And then they got
            bored with those songs — even though those songs, by any modern standard, were
            already very good. "Even the old songs are very unique because they wanted to make
            rock and roll also unique." When you have played every song that exists, you start
            writing songs that don't exist yet. That's the Beatles.
          </p>
          <p>
            Their approach to learning was equally unusual. "They always wanted to learn, but
            they never wanted to learn in a very textbook way. So they would find guys near the
            bus station, near the bus stop playing guitar, and they would say, how does he play
            chords in such a clean way? You know, and they'll just go and learn from that."
          </p>
          <p>
            The famous story, as Paul McCartney tells it: as teenagers they went right across
            Liverpool by bus to find a man who knew how to play a B7 chord on the guitar. Not a
            teacher — just someone who knew the chord. That is how they chose to learn.
          </p>
        </Prose>

        <TheoryBox label="The sitar and Norwegian Wood">
          George Harrison picked up the sitar in 1965 and played it on "Norwegian Wood" (Rubber
          Soul, 1965) as a beginner — before his lessons with Pandit Ravi Shankar began in 1966,
          and well before the band's 1968 stay in Rishikesh. What he played was not what a
          professional sitarist would play. It was just enough for that tune. Learn just enough
          from any source. Use it immediately. This is the Beatles method.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="A different creature" title="What happens when Lennon and McCartney write together">
        <Prose>
          <p>
            Jason draws a distinction that matters for anyone trying to understand Beatles
            harmony. John Lennon alone writes in a certain way. Paul McCartney alone writes in
            a certain way. When they write together, the result is neither Lennon's voice nor
            McCartney's voice. "Like a different creature got built."
          </p>
          <p>
            Four composers in one band — all equally skilled at something — is unusual enough.
            But that they collaborated in pairs, and that the collaboration produced a sound
            that belonged to neither individual, is what makes the Beatles catalogue so difficult
            to categorise. If you study more Beatles songs, Jason says, "you learn a lot more
            with respect to harmony."
          </p>
          <p>
            The last album was also marked by something else: "The beauty of their last album
            was they didn't even want to perform it. They were just happy recording it. They
            were just happy in the studio." The fame had become irrelevant. The layers you could
            get in the studio, the technology that George Martin brought — that was the point.
            A rock and roll band that became fascinated by recording itself. By the time of
            "Because," the song did not need to be performed live. It only needed to exist on tape.
          </p>
        </Prose>

        <NoteImage
          src="/notes/the-beatles/finals/14-because-pt-2.png"
          caption="Jason's chord analysis of 'Because' Part 2 — the LHMH arpeggio pattern notated, the D major modulation marked, and the D major flat 5 → D diminished move highlighted."
        />

        <MTQuote>
          "They are modulating to D which is a semitone above C sharp minor. Who on earth will
          ever do that? It's a semitone above the tonic. That's one of the probably the scariest
          notes you would ever want to modulate to." — Jason on the move that makes 'Because' unique.
        </MTQuote>
      </Chapter>
    </LessonShell>
  );
}
